import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { ERROR_INTERCEPTOR_BYPASS, HANDLE_ERROR_AND_THROW } from '@helios/utils'

/** Mirrors commentary-ws `CountsResponse` (Kotlin). */
export interface CountsResponseDto {
  counts: Record<string, number>
}

export const ACCOUNT_LINK_TYPE = 'account'

export function commentaryCountKey(
  linkType: string,
  id: string | undefined,
): string {
  return `${linkType}:${id}`
}

/** Mirrors commentary-ws `BulkCountsResponse` (Kotlin). `counts` is keyed by
 *  accountId; `failed` lists requested ids the caller isn't a member of. */
export interface BulkCountsResponseDto {
  counts: Record<string, Record<string, number>>
  failed?: string[]
}

export const COMMENTARY_COUNTS_URL =
  '/plugin/commentary-ws/external/comments/counts'

export const COMMENTARY_BULK_COUNTS_URL = `${COMMENTARY_COUNTS_URL}/bulk`

@Injectable({ providedIn: 'root' })
export class HttpCommentaryCountsService {
  constructor(private readonly http: HttpClient) {}

  // HANDLE_ERROR_AND_THROW so ErrorInterceptor rethrows instead of swallowing,
  // letting the effect's catchError dispatch commentaryCountsLoadFailed and
  // clear the loading flag.
  //
  // ERROR_INTERCEPTOR_BYPASS for 403: this fans out one request per account
  // the user has ANY access to, and lacking commentary access on a given
  // account is a completely normal, expected outcome — not a real error.
  // Without the bypass, ErrorInterceptor logs each one via console.error and
  // pushes it into the app-wide error log (surfaced as the header's error
  // badge), so a user with commentary access to only a few of their accounts
  // sees the badge climb by one per inaccessible account on every load. The
  // bypass still rethrows (see interceptor), so commentaryCountsLoadFailed
  // still fires — only the logging/reporting side is skipped. Other
  // statuses (a genuine 5xx, network failure) are unaffected and still get
  // reported as before.
  fetchCounts(accountId: string): Observable<CountsResponseDto> {
    return this.http.get<CountsResponseDto>(COMMENTARY_COUNTS_URL, {
      params: { accountId },
      context: new HttpContext()
        .set(HANDLE_ERROR_AND_THROW, true)
        .set(ERROR_INTERCEPTOR_BYPASS, [403]),
    })
  }

  /** Bulk variant of fetchCounts — one request for many accounts (e.g. an
   *  aggregate's subaccounts) instead of fanning out one HTTP call per account.
   *
   *  Bypasses reporting for 403 and 404 only, deliberately narrower than a blanket list.
   *  This call carries the counts traffic for every account grid, so an unbypassed status
   *  pushes an entry into the app-wide error log for a fetch the user never asked for. 403
   *  is the normal answer for a caller without commentary access; 404 is the expected answer
   *  until commentary-ws ships this route. A 5xx is a genuine fault and still reports. */
  fetchBulkCounts(accountIds: string[]): Observable<BulkCountsResponseDto> {
    return this.http.post<BulkCountsResponseDto>(
      COMMENTARY_BULK_COUNTS_URL,
      { accountIds },
      {
        context: new HttpContext()
          .set(HANDLE_ERROR_AND_THROW, true)
          .set(ERROR_INTERCEPTOR_BYPASS, [403, 404]),
      },
    )
  }
}
